import { Router } from 'express';
import { db } from '../storage';
import { SalarySummary } from '../../src/types';

export const salaryRouter = Router();

salaryRouter.get('/summary', (req, res) => {
  const teacherId = req.query.teacherId as string;
  const month = (req.query.month as string) || new Date().toISOString().substring(0, 7);
  if (!teacherId) return res.status(400).json({ error: 'Missing teacherId' });

  const user = db.getUserById(teacherId);
  if (!user) return res.status(404).json({ error: 'Teacher not found' });

  const classes = db.getClasses(teacherId);
  const students = db.getStudents(teacherId);
  const sessions = db.getSessions(teacherId, month);

  const completedSessions = sessions.filter(s => s.status === 'completed');

  let totalTeachingHours = 0;
  completedSessions.forEach(s => {
    const [sh, sm] = s.startTime.split(':').map(Number);
    const [eh, em] = s.endTime.split(':').map(Number);
    const durationHours = Math.max(0.5, (eh * 60 + em - (sh * 60 + sm)) / 60);
    totalTeachingHours += durationHours;
  });

  const totalStudentsAttended = completedSessions.reduce((acc, s) => acc + (s.actualStudentsPresent || 0), 0);

  // Group by class breakdown
  const classBreakdowns = classes.map(c => {
    const classCompleted = completedSessions.filter(s => s.classId === c.id);
    const classHours = classCompleted.reduce((sum, s) => {
      const [sh, sm] = s.startTime.split(':').map(Number);
      const [eh, em] = s.endTime.split(':').map(Number);
      return sum + Math.max(0.5, (eh * 60 + em - (sh * 60 + sm)) / 60);
    }, 0);

    const classStudents = students.filter(s => s.classId === c.id);
    const classTuitionTotal = classStudents.reduce((sum, s) => sum + (s.tuitionFeePaid || 0), 0);

    let earnings = 0;
    if (user.salaryCalculationType === 'revenue_share') {
      earnings = (classTuitionTotal * (c.teacherSharePercent || user.defaultRevenueSharePercent || 100)) / 100;
    } else if (user.salaryCalculationType === 'per_student') {
      const ratePerStudent = c.pricePerStudentPerSession || user.defaultRatePerStudent || 40000;
      earnings = classCompleted.reduce((sum, s) => sum + (s.actualStudentsPresent || classStudents.length) * ratePerStudent, 0);
    } else {
      // per_session default
      earnings = classCompleted.reduce((sum, s) => sum + (s.earnedAmount || c.pricePerSession), 0);
    }

    return {
      classId: c.id,
      className: c.name,
      completedSessions: classCompleted.length,
      hours: Math.round(classHours * 10) / 10,
      earnings: Math.round(earnings),
      tuitionTotal: classTuitionTotal,
      studentCount: classStudents.length
    };
  });

  const estimatedEarnings = classBreakdowns.reduce((sum, b) => sum + b.earnings, 0);
  const collectedTuition = students.reduce((sum, s) => sum + (s.tuitionFeePaid || 0), 0);
  const pendingTuition = students.reduce((sum, s) => sum + Math.max(0, (s.tuitionFeeDue || 0) - (s.tuitionFeePaid || 0)), 0);

  const summary: SalarySummary = {
    month,
    totalCompletedSessions: completedSessions.length,
    totalScheduledSessions: sessions.length,
    totalTeachingHours: Math.round(totalTeachingHours * 10) / 10,
    totalStudentsAttended,
    estimatedEarnings,
    collectedTuition,
    pendingTuition,
    classBreakdowns
  };

  res.json(summary);
});
